import api from './api';

// ============================================
// TIPOS PARA CONTRATOS
// ============================================

export interface Contrato {
  id: string;
  numero?: string;
  titulo: string;
  status: 'rascunho' | 'ativo' | 'cancelado' | 'finalizado';
  dataInicio: string;
  dataFim?: string | null;
  diaVencimento: number;
  valorMensal?: number | null;
  conteudo: string;
  publicadoPortal: boolean;
  clientId: string;
  client: {
    id: string;
    razaoSocial: string;
    nomeFantasia?: string;
    cnpj?: string;
  };
  faturasGeradas?: Array<{
    id: string;
    numero?: string;
    status: string;
    valorTotal: number;
    dataVencimento: string;
  }>;
  _count?: {
    faturasGeradas: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ContratoInput {
  clientId: string;
  titulo: string;
  dataInicio: string;
  dataFim?: string | null;
  diaVencimento: number;
  valorMensal?: number | null;
  conteudo: string;
}

export interface ContratoFilters {
  clientId?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ContratoListResponse {
  data: Contrato[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface MRRResponse {
  mrr: number;
  quantidadeContratos: number;
}

export interface FaturaContrato {
  id: string;
  numero: number;
  status: string;
  dataEmissao: string;
  dataVencimento: string;
  valorTotal: number;
  itens: Array<{
    id: number;
    descricao: string;
    quantidade: number;
    valorUnit: number;
    subtotal: number;
  }>;
}

// ============================================
// TIPOS PARA MODELOS DE CONTRATO
// ============================================

export interface ModeloContrato {
  id: string;
  titulo: string;
  tipoNegocio?: string | null;
  conteudo: string;
  createdAt: string;
  updatedAt: string;
}

export interface ModeloContratoInput {
  titulo: string;
  tipoNegocio?: string;
  conteudo: string;
}

// ============================================
// VARIÁVEIS DINÂMICAS
// ============================================

export const VARIAVEIS_DISPONIVEIS = [
  // Dados do cliente (CONTRATANTE)
  { variavel: '{{CLIENTE_NOME}}', descricao: 'Nome/Razão Social do cliente (CONTRATANTE)', grupo: 'cliente' },
  { variavel: '{{CLIENTE_CNPJ}}', descricao: 'CNPJ do cliente formatado', grupo: 'cliente' },
  // Dados da empresa (CONTRATADA)
  { variavel: '{{EMPRESA_NOME}}', descricao: 'Nome da sua empresa (CONTRATADA)', grupo: 'empresa' },
  { variavel: '{{EMPRESA_CNPJ}}', descricao: 'CNPJ da sua empresa', grupo: 'empresa' },
  { variavel: '{{CIDADE_EMPRESA}}', descricao: 'Cidade da empresa contratada', grupo: 'empresa' },
  // Dados do contrato
  { variavel: '{{CONTRATO_NUMERO}}', descricao: 'Número sequencial do contrato', grupo: 'contrato' },
  { variavel: '{{VALOR}}', descricao: 'Valor mensal do contrato (R$)', grupo: 'contrato' },
  { variavel: '{{DATA_HOJE}}', descricao: 'Data atual (DD/MM/YYYY)', grupo: 'contrato' },
  { variavel: '{{DATA_INICIO}}', descricao: 'Data de início do contrato', grupo: 'contrato' },
  { variavel: '{{DATA_FIM}}', descricao: 'Data de fim ou "Indeterminado"', grupo: 'contrato' },
  { variavel: '{{DIA_VENCIMENTO}}', descricao: 'Dia do vencimento da fatura', grupo: 'contrato' },
];

// ============================================
// SERVIÇO DE CONTRATOS
// ============================================

export const contratoService = {
  async proximoNumero(): Promise<string> {
    const response = await api.get<{ numero: string }>('/contratos/proximo-numero');
    return response.data.numero;
  },

  async list(filters?: ContratoFilters): Promise<ContratoListResponse> {
    const params = new URLSearchParams();
    if (filters?.clientId) params.append('clientId', filters.clientId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/contratos?${params}`);
    return response.data;
  },

  async getById(id: string): Promise<Contrato> {
    const response = await api.get(`/contratos/${id}`);
    return response.data;
  },

  async create(data: ContratoInput): Promise<Contrato> {
    const response = await api.post('/contratos', data);
    return response.data;
  },

  async update(id: string, data: Partial<ContratoInput>): Promise<Contrato> {
    const response = await api.put(`/contratos/${id}`, data);
    return response.data;
  },

  async updateStatus(id: string, status: string): Promise<Contrato> {
    const response = await api.patch(`/contratos/${id}/status`, { status });
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/contratos/${id}`);
  },

  async getFaturas(id: string): Promise<FaturaContrato[]> {
    const response = await api.get(`/contratos/${id}/faturas`);
    return response.data;
  },

  async getMRR(): Promise<MRRResponse> {
    const response = await api.get('/contratos/mrr');
    return response.data;
  },

  async downloadPdf(id: string, titulo?: string): Promise<void> {
    const response = await api.get(`/contratos/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `contrato-${titulo || id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // ---------- Itens do Contrato ----------
  async listItens(contratoId: string): Promise<ContratoItem[]> {
    const response = await api.get<ContratoItem[]>(`/contratos/${contratoId}/itens`);
    return response.data;
  },
  async createItem(contratoId: string, data: ContratoItemInput): Promise<ContratoItem> {
    const response = await api.post<ContratoItem>(`/contratos/${contratoId}/itens`, data);
    return response.data;
  },
  async updateItem(contratoId: string, itemId: number, data: Partial<ContratoItemInput>): Promise<ContratoItem> {
    const response = await api.put<ContratoItem>(`/contratos/${contratoId}/itens/${itemId}`, data);
    return response.data;
  },
  async deleteItem(contratoId: string, itemId: number): Promise<void> {
    await api.delete(`/contratos/${contratoId}/itens/${itemId}`);
  },
};

// ============================================
// TIPOS PARA ITENS DO CONTRATO
// ============================================

export interface ContratoItem {
  id: number;
  contratoId: string;
  produtoServicoId: number | null;
  produtoServico?: {
    id: number;
    codigo: string | null;
    nome: string;
    unidade: string;
    valorUnit: number;
  } | null;
  descricao: string;
  quantidade: number;
  valorUnit: number;
  tipoValor: 'fixo' | 'variavel';
  ordem: number;
}

export interface ContratoItemInput {
  produtoServicoId?: number | null;
  descricao: string;
  quantidade: number;
  valorUnit: number;
  tipoValor: 'fixo' | 'variavel';
  ordem?: number;
}

// ============================================
// SERVIÇO DE MODELOS DE CONTRATO
// ============================================

export const modeloContratoService = {
  async list(filters?: { categoria?: string; tipo?: string }): Promise<ModeloContrato[]> {
    const params = new URLSearchParams();
    if (filters?.categoria) params.append('categoria', filters.categoria);
    if (filters?.tipo) params.append('tipo', filters.tipo);

    const response = await api.get(`/modelos-contrato?${params}`);
    return response.data;
  },

  async getById(id: string): Promise<ModeloContrato> {
    const response = await api.get(`/modelos-contrato/${id}`);
    return response.data;
  },

  async create(data: ModeloContratoInput): Promise<ModeloContrato> {
    const response = await api.post('/modelos-contrato', data);
    return response.data;
  },

  async update(id: string, data: Partial<ModeloContratoInput>): Promise<ModeloContrato> {
    const response = await api.put(`/modelos-contrato/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/modelos-contrato/${id}`);
  },
};

// ============================================
// UTILITÁRIOS
// ============================================

/**
 * Formata um valor numérico como moeda brasileira (R$)
 */
export function formatarMoeda(valor: number): string {
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Formata uma data no padrão brasileiro DD/MM/YYYY
 * Corrige problema de fuso horário forçando leitura em UTC
 */
export function formatarData(data: string | Date): string {
  if (typeof data === 'string') {
    // Para strings ISO, forçar interpretação em UTC para evitar offset de timezone
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }
  // Para objetos Date, usar diretamente
  return data.toLocaleDateString('pt-BR');
}

/**
 * Formata CNPJ no padrão XX.XXX.XXX/XXXX-XX
 */
export function formatarCNPJ(cnpj: string): string {
  const numeros = cnpj.replace(/\D/g, '');
  if (numeros.length !== 14) return cnpj;
  return numeros.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
}

/**
 * Substitui variáveis dinâmicas no conteúdo do contrato
 */
export function substituirVariaveis(
  conteudo: string,
  cliente: { razaoSocial: string; nomeFantasia?: string | null; cnpj: string },
  contrato: { valorMensal?: number | null; dataInicio: string; dataFim?: string | null; diaVencimento: number; numero?: string },
  empresa?: { nome?: string; nomeFantasia?: string | null; razaoSocial?: string; cnpj?: string; cidade?: string } | null
): string {
  const hoje = new Date();

  const variaveis: Record<string, string> = {
    // Cliente (CONTRATANTE)
    '{{CLIENTE_NOME}}': cliente.nomeFantasia || cliente.razaoSocial,
    '{{CLIENTE_CNPJ}}': formatarCNPJ(cliente.cnpj),
    // Empresa (CONTRATADA)
    '{{EMPRESA_NOME}}': empresa?.nome || empresa?.nomeFantasia || empresa?.razaoSocial || '[EMPRESA]',
    '{{EMPRESA_CNPJ}}': empresa?.cnpj ? formatarCNPJ(empresa.cnpj) : '[CNPJ DA EMPRESA]',
    '{{CIDADE_EMPRESA}}': empresa?.cidade || '[CIDADE]',
    // Contrato
    '{{CONTRATO_NUMERO}}': contrato.numero || '[NÚMERO]',
    '{{VALOR}}': contrato.valorMensal != null ? formatarMoeda(contrato.valorMensal) : 'A definir',
    '{{DATA_HOJE}}': formatarData(hoje),
    '{{DATA_INICIO}}': formatarData(contrato.dataInicio),
    '{{DATA_FIM}}': contrato.dataFim ? formatarData(contrato.dataFim) : 'Indeterminado',
    '{{DIA_VENCIMENTO}}': String(contrato.diaVencimento),
  };

  let resultado = conteudo;
  for (const [variavel, valor] of Object.entries(variaveis)) {
    resultado = resultado.split(variavel).join(valor);
  }

  return resultado;
}

/**
 * Retorna a cor do badge baseado no status do contrato
 */
export function getStatusColor(status: string): string {
  const cores: Record<string, string> = {
    rascunho: 'bg-gray-100 text-gray-800',
    ativo: 'bg-green-100 text-green-800',
    cancelado: 'bg-red-100 text-red-800',
    finalizado: 'bg-blue-100 text-blue-800',
  };
  return cores[status] || 'bg-gray-100 text-gray-800';
}

/**
 * Retorna o label do status do contrato
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    rascunho: 'Rascunho',
    ativo: 'Ativo',
    cancelado: 'Cancelado',
    finalizado: 'Finalizado',
  };
  return labels[status] || status;
}
